<template>
  <v-dialog v-model="createBillingOpen" width="1300" @click:outside="close">
    <v-card :disabled="getIsAttachAccountLoading">
      <template v-if="getAccountSearch.length">
        <v-card-title>Create Billing Account</v-card-title>

        <v-card-text>
          <v-row>
            <v-col>
              <span>These billing accounts look like they might be related</span>
              <v-data-table :headers="headers" :items="getAccountSearch" disable-pagination hide-default-footer>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn x-small color="secondary" class="mr-3" :href="`/accounts/${item.account_id}/`" target="_blank">
                    <v-icon small>{{ mdiOpenInNew }}</v-icon>
                  </v-btn>

                  <v-btn x-small color="primary" @click="useAccount(currentLead.id, item.account_id)">Use Account</v-btn>
                </template>

              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </template>

      <template v-else>
        <v-card-text>
          <v-row>
            <v-col>
              <span>No related billing accounts were found</span>
            </v-col>
          </v-row>
        </v-card-text>
      </template>

      <v-card-text>
        <span>Create new billing account</span>
        <v-row>

          <v-col>
            <v-row class="mb-n6">
              <v-col>
                <v-text-field v-model="business_name" label="Business Name" :error-messages="businessNameErrors" @change="$v.business_name.$touch()" @blur="$v.business_name.$touch()" :disabled="createBillingLoading" outlined dense required></v-text-field>
              </v-col>
            </v-row>

            <v-row class="mb-n6">
              <v-col>
                <v-text-field v-model="first_name" label="First Name" :error-messages="firstNameErrors" @change="$v.first_name.$touch()" @blur="$v.first_name.$touch()" :disabled="createBillingLoading" outlined dense required></v-text-field>
              </v-col>
              <v-col>
                <v-text-field v-model="last_name" label="Last Name" :error-messages="lastNameErrors" @change="$v.last_name.$touch()" @blur="$v.last_name.$touch()" :disabled="createBillingLoading" outlined dense required></v-text-field>
              </v-col>
            </v-row>

            <v-row class="mb-n6">
              <v-col>
                <v-text-field v-model="email_address" label="Email Address" :error-messages="emailAddressErrors" @change="$v.email_address.$touch()" @blur="$v.email_address.$touch()" :disabled="createBillingLoading" outlined dense required></v-text-field>
              </v-col>
            </v-row>

            <v-row class="mb-n6">
              <v-col>
                <v-text-field v-model="customer_phone" label="Customer Phone" :disabled="createBillingLoading" outlined dense required></v-text-field>
              </v-col>
            </v-row>

            <v-row class="mb-n6">
              <v-col>
                <v-text-field v-model="mobile_phone" label="Mobile Phone" :error-messages="mobilePhoneErrors" @change="$v.mobile_phone.$touch()" @blur="$v.mobile_phone.$touch()" :disabled="createBillingLoading" outlined dense required></v-text-field>
              </v-col>
            </v-row>
          </v-col>

          <v-col>
            <AddressForm @addressSelected="onAddressChanged" :disabled="createBillingLoading" :passedAddress="passedAddress" :edit="false" />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" small :loading="getIsAttachAccountLoading || createBillingLoading" @click="submit">SAVE</v-btn>
        <v-btn color="primary" small outlined :disabled="getIsAttachAccountLoading || createBillingLoading" @click="close">CANCEL</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapState,mapActions,mapMutations,mapGetters } from "vuex";
  import { validationMixin } from "vuelidate";
  import { required,email } from "vuelidate/lib/validators";
  import AddressForm from "@/components/Misc/AddressForm";
  import { mdiOpenInNew } from '@mdi/js';

  export default {
    components: { AddressForm },

    mixins: [ validationMixin ],

    validations: {
      business_name: { required },
      first_name: { required },
      last_name: { required },
      mobile_phone: { required },
      email_address: { email,required },
    },

    data() {
      return {
        business_name: '',
        first_name: '',
        last_name: '',
        mobile_phone: '',
        customer_phone: '',
        email_address: '',
        address: {
          city: "",
          country: "",
          county: "",
          line_one: "",
          line_two: "",
          post_code: "",
        },
        passedAddress: {
          city: "",
          country: "",
          county: "",
          line_one: "",
          line_two: "",
          post_code: "",
        },
        headers: [
          { text: 'Account ID', value: 'account_id' },
          { text: 'Business Name', value: 'business_name' },
          { text: 'Email Address', value: 'email_address' },
          { text: 'Phone Number', value: 'phone_number' },
          { text: 'Mobile Number', value: 'mobile_number' },
          { text: '', value: 'actions' },
        ],
        mdiOpenInNew,
      }
    },

    computed: {
      ...mapState('leads',['createBillingLoading','currentLead','createBillingOpen']),
      ...mapGetters('leads/accountSearch',['getAccountSearch', 'getIsAttachAccountLoading']),
      ...mapGetters('leads/accountAttach',['getIsAttachAccountLoading']),

      businessNameErrors() {
        const errors = [];
        if (!this.$v.business_name.$dirty) return errors;
        !this.$v.business_name.required && errors.push("Business Name is required");
        return errors;
      },

      emailAddressErrors() {
        const errors = [];
        if (!this.$v.email_address.$dirty) return errors;
        !this.$v.email_address.email && errors.push("Email address is not valid");
        !this.$v.email_address.required && errors.push("Email address is required");
        return errors;
      },

      firstNameErrors() {
        const errors = [];
        if (!this.$v.first_name.$dirty) return errors;
        !this.$v.first_name.required && errors.push("First Name is required");
        return errors;
      },

      lastNameErrors() {
        const errors = [];
        if (!this.$v.last_name.$dirty) return errors;
        !this.$v.last_name.required && errors.push("Last Name is required");
        return errors;
      },

      mobilePhoneErrors() {
        const errors = [];
        if (!this.$v.mobile_phone.$dirty) return errors;
        !this.$v.mobile_phone.required && errors.push("Mobile Phone is required");
        return errors;
      },
    },

    watch: {
      createBillingOpen(val) {
        if(val){
          let CL = this.currentLead;
          let names = CL.contact.name.split(' ');
          let namesLength = names.length;
          this.first_name = names[0];
          this.last_name = names[1];
          if(namesLength>2){
            this.last_name = names[namesLength-1]; names.pop();
            this.first_name = names.join(' ');
          }
          this.business_name = CL.business_name;
          this.mobile_phone = CL.contact.mobile_number;
          this.customer_phone = CL.contact.phone_number;
          this.email_address = CL.contact.email_address;
          this.passedAddress.post_code = CL.address_post_code;
        }
      }
    },

    methods: {
      ...mapActions('leads',['createBillingAccount','getLeadByID']),
      ...mapActions('leads/accountAttach',['attachAccount']),
      ...mapActions('accounts', ['fetchAccountBalance']),
      ...mapMutations('leads',['SET_CREATE_BILLING_OPEN']),

      async submit() {
        this.$v.$touch();
        if(!this.$v.$error){
          let data = {
            business_name: this.business_name,
            first_name: this.first_name,
            last_name: this.last_name,
            mobile_number: this.mobile_phone,
            phone_number: this.customer_phone,
            email_address: this.email_address,
            address_line_one: this.address.line_one,
            address_line_two: this.address.line_two,
            address_city: this.address.city,
            address_region: this.address.county,
            address_post_code: this.address.post_code,
            address_country: this.address.country,
          };
          await this.createBillingAccount({id:this.currentLead.id,data:data});
        }
      },

      async useAccount(lead_id, account_id) {
        if(confirm('Are you sure that this lead is related to this billing account?')) {
          await this.attachAccount({ lead_id: lead_id, account_id: account_id });
          await this.getLeadByID({ id: this.currentLead.id });
          await this.fetchAccountBalance(this.currentLead.account_id);
          this.close();
        }
      },

      close() {
        this.SET_CREATE_BILLING_OPEN(false);
      },

      onAddressChanged(address) {
        this.address = address;
      },
    },
  }
</script>

<style lang="scss" scoped></style>
